/**
 * FsmVertexHandles — draggable bend-point handles for a selected FSM transition
 * (phase 6d, step 2).
 *
 * The FSM counterpart of `VertexHandles` (the EDG). Same affordance, same
 * commit-on-drag-end discipline:
 *   - a SELECTED transition shows a small circular handle at each persisted
 *     vertex,
 *   - dragging a handle moves that vertex (live, the path follows),
 *   - double-clicking a handle removes that vertex,
 *   - clicking a handle selects it; Delete / Backspace then removes it (the
 *     click must not reach the edge, or Delete hits the whole edge),
 *   - shift+click / double-click on the PATH inserts a new vertex (handled
 *     upstream in FsmFlow, ported from the Rappid LinkView gesture).
 *
 * The only FSM-specific twist is the self-loop 2-vertex invariant: a transition
 * whose source === target must keep at least 2 vertices (fsmlink.js
 * `fixVertices`, called from `setVertices`). The STORE enforces this — a remove
 * that would drop a self-loop below 2 vertices is re-filled by `fixVertices` on
 * commit. To avoid the visual jump of removing then re-adding, we also refuse the
 * remove client-side when it would breach the invariant (matching the Rappid
 * paper, whose self-loops never lost their 2 vertices).
 *
 * Like the EDG handles, the new point is reported up through `onVertexDragLive`
 * during a drag so FsmFlow re-derives THIS edge's path from the live vertices; the
 * MST commit lands once on pointer-up through the EXISTING `updateFSMVertices`
 * command — never mid-drag. Styling reuses the `.merode-vertex-handle` class.
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React from 'react';
import { useReactFlow } from '@xyflow/react';
import type { Pt } from './edgeGeometry';
import { EDIT_BEND_POINT_HINT, isVertexDrag, moveVertex, removeVertex } from './vertexEditing';

export type FsmVertexDragLive = (
  transitionId: string,
  vertices: Pt[] | null
) => void;

export type FsmVertexHandlesProps = {
  /** The transition id. */
  edgeId: string;
  /** Whether this transition is a self-loop (source === target). */
  selfLoop: boolean;
  /**
   * The vertices to render handles for. During a drag this is the LIVE array
   * (FsmFlow overrides it from `onVertexDragLive`), otherwise the persisted one.
   */
  vertices: Pt[];
  /** Commit a new vertices array (the existing `updateFSMVertices` command). */
  onChangeVertices: (transitionId: string, vertices: Pt[]) => void;
  /** Report the live vertices during a drag (or null on drag end / no drag). */
  onVertexDragLive: FsmVertexDragLive;
  selectedIndex?: number | null;
  onSelectVertex?: (transitionId: string, index: number) => void;
  onDeleteVertex?: (transitionId: string, index: number) => void;
};

const HANDLE_HIT_RADIUS = 22;
const HANDLE_DOT_RADIUS = 5;
// A self-loop must keep at least 2 vertices (fsmlink.js fixVertices).
const SELF_LOOP_MIN_VERTICES = 2;

export default function FsmVertexHandles({
  edgeId,
  selfLoop,
  vertices,
  onChangeVertices,
  onVertexDragLive,
  selectedIndex = null,
  onSelectVertex,
  onDeleteVertex,
}: FsmVertexHandlesProps) {
  const { screenToFlowPosition } = useReactFlow();

  // Latest-prop refs so the window-level listeners (bound once per drag) read
  // fresh values without re-binding.
  const verticesRef = React.useRef(vertices);
  verticesRef.current = vertices;
  const liveRef = React.useRef(onVertexDragLive);
  liveRef.current = onVertexDragLive;
  const commitRef = React.useRef(onChangeVertices);
  commitRef.current = onChangeVertices;
  const screenToFlowRef = React.useRef(screenToFlowPosition);
  screenToFlowRef.current = screenToFlowPosition;

  const handlePointerDown = React.useCallback(
    (evt: React.PointerEvent, index: number) => {
      if (evt.button !== 0) return;
      evt.stopPropagation();
      evt.preventDefault();
      onSelectVertex?.(edgeId, index);

      const base = verticesRef.current.map((v) => ({ x: v.x, y: v.y }));

      // A click (no real travel) must not move the bend: see isVertexDrag.
      const start = { x: evt.clientX, y: evt.clientY };
      let dragging = false;

      const onMove = (moveEvt: PointerEvent) => {
        if (!dragging && !isVertexDrag(start, { x: moveEvt.clientX, y: moveEvt.clientY })) return;
        dragging = true;
        const flow = screenToFlowRef.current({ x: moveEvt.clientX, y: moveEvt.clientY });
        const next = moveVertex(base, index, flow);
        liveRef.current(edgeId, next);
      };

      const onUp = (upEvt: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (!dragging) return;
        const flow = screenToFlowRef.current({ x: upEvt.clientX, y: upEvt.clientY });
        const point = {
          x: Math.round(flow.x),
          y: Math.round(flow.y),
        };
        const next = moveVertex(base, index, point);
        // Clear the live override BEFORE committing so the re-derived path uses
        // the committed (persisted) vertices, not a stale transient array.
        liveRef.current(edgeId, null);
        if (base[index].x !== point.x || base[index].y !== point.y) {
          commitRef.current(edgeId, next);
        }
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [edgeId, onSelectVertex]
  );

  // Double-click removes a vertex (the Rappid default LinkView gesture). For a
  // self-loop we refuse to drop below the 2-vertex invariant (the store would
  // re-fill it anyway via fixVertices; refusing avoids the visual flicker).
  const handleDoubleClick = React.useCallback(
    (evt: React.MouseEvent, index: number) => {
      evt.stopPropagation();
      evt.preventDefault();
      const current = verticesRef.current;
      if (selfLoop && current.length <= SELF_LOOP_MIN_VERTICES) return;
      if (onDeleteVertex) {
        onDeleteVertex(edgeId, index);
      } else {
        onChangeVertices(
          edgeId,
          removeVertex(current, index, {
            minVertices: selfLoop ? SELF_LOOP_MIN_VERTICES : undefined,
          })
        );
      }
    },
    [onChangeVertices, onDeleteVertex, edgeId, selfLoop]
  );

  if (vertices.length === 0) return null;

  return (
    <g className="merode-vertex-handles">
      {vertices.map((vertex, index) => (
        <g
          key={`${edgeId}-v${index}`}
          className={[
            'merode-vertex-handle',
            selectedIndex === index ? 'is-selected' : '',
            'nodrag',
            'nopan',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label="Bend point"
          onPointerDown={(evt) => handlePointerDown(evt, index)}
          // Keep the click off React Flow's edge click handler: it clears the
          // vertex selection, so Delete would then delete the whole edge.
          onClick={(evt) => evt.stopPropagation()}
          onDoubleClick={(evt) => handleDoubleClick(evt, index)}
        >
          <title>{EDIT_BEND_POINT_HINT}</title>
          <circle
            className="merode-vertex-handle-hit"
            cx={vertex.x}
            cy={vertex.y}
            r={HANDLE_HIT_RADIUS}
          />
          <circle
            className="merode-vertex-handle-dot"
            cx={vertex.x}
            cy={vertex.y}
            r={HANDLE_DOT_RADIUS}
          />
        </g>
      ))}
    </g>
  );
}
